/**
 * API Service for Foresight Meet Schedule Predictor
 * Handles all backend communication with device-based authentication
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Device ID management
const DEVICE_ID_KEY = 'foresight_device_id';

/**
 * Build fetch options with credentials + Bearer fallback so logged-in users
 * are authenticated regardless of cookie availability (Capacitor native often
 * blocks 3rd-party cookies; we use the cached session_token in that case).
 */
const authFetchOptions = (extra = {}) => {
  const headers = { ...(extra.headers || {}) };
  try {
    const token = localStorage.getItem('foresight_session_token');
    if (token && !headers['Authorization']) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  } catch (_) {
    /* ignore */
  }
  return {
    credentials: 'include',
    ...extra,
    headers,
  };
};

/**
 * Get or create device ID
 * This is the anonymous identifier for this device
 */
export const getDeviceId = () => {
  return localStorage.getItem(DEVICE_ID_KEY);
};

/**
 * Set device ID after registration
 */
export const setDeviceId = (deviceId) => {
  localStorage.setItem(DEVICE_ID_KEY, deviceId);
};

/**
 * Register device with backend
 * Call this on app startup to ensure we have a valid device ID
 */
export const registerDevice = async (platform = 'web') => {
  const existingDeviceId = getDeviceId();
  
  try {
    const response = await fetch(`${API_URL}/api/device/register`, authFetchOptions({
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        device_id: existingDeviceId,
        platform: platform
      })
    }));
    
    if (!response.ok) {
      throw new Error('Failed to register device');
    }
    
    const data = await response.json();
    setDeviceId(data.device_id);
    
    return data;
  } catch (error) {
    console.error('Device registration error:', error);
    
    // Fallback: generate local device ID if backend is unavailable
    if (!existingDeviceId) {
      const fallbackId = `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      setDeviceId(fallbackId);
      return { device_id: fallbackId, is_new: true, offline: true };
    }
    
    return { device_id: existingDeviceId, is_new: false, offline: true };
  }
};

// ============== MEETS API ==============

/**
 * Get all meets for current device
 */
export const getMeets = async () => {
  const deviceId = getDeviceId();
  
  // Get localStorage meets first
  const localMeets = JSON.parse(localStorage.getItem('foresight_meets') || '[]');
  
  if (!deviceId) {
    return localMeets;
  }
  
  try {
    const response = await fetch(`${API_URL}/api/meets?device_id=${encodeURIComponent(deviceId)}`, authFetchOptions());
    
    if (!response.ok) {
      throw new Error('Failed to fetch meets');
    }
    
    const data = await response.json();
    const apiMeets = data.meets || [];

    // Merge: API meets take priority for fresh data. We do NOT purge
    // previously-synced local meets just because the API response didn't
    // include them — that response can be incomplete during auth-state
    // churn (Supabase JWT expired/refreshing → backend `_meet_owner_filter`
    // returns anonymous-scoped meets only → logged-in meets temporarily
    // missing from this read). Real deletes go through `apiDeleteMeet`,
    // which surgically removes by id. Production repro that motivated
    // this conservative merge: meet running >1.5h, app offline, JWT
    // expired, user logs back in → `getMeets` returned a partial list →
    // we used to wipe the cached meet, then Dashboard saw "meet missing"
    // and nuked the live-meet pointer. See PRD invariant.
    const apiMeetIds = new Set(apiMeets.map(m => String(m.id)));
    const localExtras = localMeets.filter((m) => !apiMeetIds.has(String(m.id)));

    // Return combined list (API meets first for freshness)
    const allMeets = [...apiMeets, ...localExtras];

    // Persist the merged list. Local extras (offline-only or temporarily
    // not-returned by API) survive this write.
    localStorage.setItem('foresight_meets', JSON.stringify(allMeets));

    return allMeets;
  } catch (error) {
    console.error('Get meets error:', error);
    // Fallback to localStorage
    return localMeets;
  }
};

/**
 * Get a single meet by ID (public — no device_id required for shared links)
 */
export const getMeet = async (meetId) => {
  const deviceId = getDeviceId();

  // First check localStorage for immediate response (native/offline)
  const localMeets = JSON.parse(localStorage.getItem('foresight_meets') || '[]');
  const localMeet = localMeets.find(m => String(m.id) === String(meetId));

  try {
    const url = deviceId
      ? `${API_URL}/api/meets/${meetId}?device_id=${encodeURIComponent(deviceId)}`
      : `${API_URL}/api/meets/${meetId}`;
    const response = await fetch(url, authFetchOptions());

    if (!response.ok) {
      if (response.status === 404) {
        return localMeet || null;
      }
      throw new Error('Failed to fetch meet');
    }

    const meet = await response.json();
    return meet;
  } catch (error) {
    console.error('Get meet error:', error);
    return localMeet || null;
  }
};

/**
 * Verify whether a meet still exists on the backend.
 *
 * Returns one of:
 *   { exists: true  }                    — server confirmed the meet is there
 *   { exists: false, reason: 'notfound' } — server returned 404
 *   { exists: null,  reason: 'unknown' }  — network error / non-200/404 / offline.
 *                                           Caller MUST treat this as "do not
 *                                           purge local state".
 *
 * Critical: this endpoint (`GET /api/meets/{id}`) is public (auth-agnostic),
 * so it is robust to Supabase JWT expiry / refresh churn. Dashboard uses
 * this to decide whether the live-meet pointer should be deleted — only
 * an explicit 404 justifies deletion. See production repro: meet running
 * >1.5h, app offline, JWT expired, user reconnects → previous code wiped
 * pointer based on `getMeets()` returning an incomplete list. Direct
 * single-meet GET sidesteps that whole class of false-positives.
 */
export const verifyMeetExists = async (meetId) => {
  if (!meetId) return { exists: null, reason: 'unknown' };
  try {
    const response = await fetch(`${API_URL}/api/meets/${encodeURIComponent(meetId)}`, authFetchOptions());
    if (response.ok) return { exists: true };
    if (response.status === 404) return { exists: false, reason: 'notfound' };
    return { exists: null, reason: 'unknown' };
  } catch (_) {
    return { exists: null, reason: 'unknown' };
  }
};

/**
 * Create a new meet
 */
export const createMeet = async (meetData) => {
  const deviceId = getDeviceId();
  if (!deviceId) {
    throw new Error('Device not registered');
  }
  
  try {
    const response = await fetch(`${API_URL}/api/meets`, authFetchOptions({
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        device_id: deviceId,
        ...meetData
      })
    }));
    
    if (!response.ok) {
      const error = await response.json();
      if (response.status === 402) {
        throw new Error('PAYWALL_REQUIRED');
      }
      throw new Error(error.detail || 'Failed to create meet');
    }
    
    const newMeet = await response.json();
    
    // Also save to localStorage for offline access
    const localMeets = JSON.parse(localStorage.getItem('foresight_meets') || '[]');
    localMeets.push(newMeet);
    localStorage.setItem('foresight_meets', JSON.stringify(localMeets));
    
    return newMeet;
  } catch (error) {
    if (error.message === 'PAYWALL_REQUIRED') {
      throw error;
    }
    
    console.error('Create meet error:', error);
    
    // Fallback: save locally only, tagged for later sync
    const localMeets = JSON.parse(localStorage.getItem('foresight_meets') || '[]');
    const newMeet = {
      ...meetData,
      id: String(Date.now()),
      device_id: deviceId,
      created: new Date().toISOString(),
      _offline: true,
      _offline_origin: 'create'
    };
    localMeets.push(newMeet);
    localStorage.setItem('foresight_meets', JSON.stringify(localMeets));
    
    return newMeet;
  }
};

/**
 * Update an existing meet
 */
export const updateMeet = async (meetId, meetData) => {
  const deviceId = getDeviceId();
  if (!deviceId) {
    throw new Error('Device not registered');
  }
  
  try {
    const response = await fetch(
      `${API_URL}/api/meets/${meetId}?device_id=${encodeURIComponent(deviceId)}`,
      authFetchOptions({
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(meetData)
      })
    );
    
    if (!response.ok) {
      throw new Error('Failed to update meet');
    }
    
    const updatedMeet = await response.json();
    
    // Also update localStorage
    const localMeets = JSON.parse(localStorage.getItem('foresight_meets') || '[]');
    const index = localMeets.findIndex(m => String(m.id) === String(meetId));
    if (index !== -1) {
      localMeets[index] = updatedMeet;
      localStorage.setItem('foresight_meets', JSON.stringify(localMeets));
    }
    
    return updatedMeet;
  } catch (error) {
    console.error('Update meet error:', error);
    
    // Fallback: update locally only, tagged for later sync
    const localMeets = JSON.parse(localStorage.getItem('foresight_meets') || '[]');
    const index = localMeets.findIndex(m => String(m.id) === String(meetId));
    if (index !== -1) {
      const existing = localMeets[index];
      localMeets[index] = {
        ...existing,
        ...meetData,
        updated: new Date().toISOString(),
        _offline: true,
        // Preserve the create origin if this meet was never pushed to backend
        _offline_origin: existing._offline_origin === 'create' ? 'create' : 'update'
      };
      localStorage.setItem('foresight_meets', JSON.stringify(localMeets));
      return localMeets[index];
    }
    
    throw error;
  }
};

/**
 * Delete a meet
 */
export const deleteMeet = async (meetId) => {
  const deviceId = getDeviceId();
  if (!deviceId) {
    throw new Error('Device not registered');
  }
  
  try {
    const response = await fetch(
      `${API_URL}/api/meets/${meetId}?device_id=${encodeURIComponent(deviceId)}`,
      authFetchOptions({ method: 'DELETE' })
    );
    
    if (!response.ok && response.status !== 404) {
      throw new Error('Failed to delete meet');
    }
    
    // Also delete from localStorage
    const localMeets = JSON.parse(localStorage.getItem('foresight_meets') || '[]');
    const filtered = localMeets.filter(m => String(m.id) !== String(meetId));
    localStorage.setItem('foresight_meets', JSON.stringify(filtered));
    
    return true;
  } catch (error) {
    console.error('Delete meet error:', error);
    
    // Fallback: delete locally only
    const localMeets = JSON.parse(localStorage.getItem('foresight_meets') || '[]');
    const filtered = localMeets.filter(m => String(m.id) !== String(meetId));
    localStorage.setItem('foresight_meets', JSON.stringify(filtered));
    
    return true;
  }
};


// ============== SETTINGS API ==============

/**
 * Get settings for current device
 */
export const getSettings = async () => {
  const deviceId = getDeviceId();
  if (!deviceId) {
    return null;
  }
  
  try {
    const response = await fetch(
      `${API_URL}/api/settings?device_id=${encodeURIComponent(deviceId)}`,
      authFetchOptions()
    );
    
    if (!response.ok) {
      throw new Error('Failed to fetch settings');
    }
    
    const data = await response.json();
    return data.settings;
  } catch (error) {
    console.error('Get settings error:', error);
    // Fallback to localStorage
    return JSON.parse(localStorage.getItem('foresight_settings') || 'null');
  }
};

/**
 * Save settings for current device
 */
export const saveSettings = async (settings) => {
  const deviceId = getDeviceId();
  if (!deviceId) {
    // Save locally only
    localStorage.setItem('foresight_settings', JSON.stringify(settings));
    return settings;
  }
  
  try {
    const response = await fetch(
      `${API_URL}/api/settings?device_id=${encodeURIComponent(deviceId)}`,
      authFetchOptions({
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings })
      })
    );
    
    if (!response.ok) {
      throw new Error('Failed to save settings');
    }
    
    // Also save to localStorage for offline access
    localStorage.setItem('foresight_settings', JSON.stringify(settings));
    
    return settings;
  } catch (error) {
    console.error('Save settings error:', error);
    // Fallback to localStorage
    localStorage.setItem('foresight_settings', JSON.stringify(settings));
    return settings;
  }
};


// ============== PURCHASE API ==============

/**
 * Get purchase status for current device
 */
export const getPurchaseStatus = async () => {
  const deviceId = getDeviceId();
  if (!deviceId) {
    return {
      is_premium: false,
      meets_created: 0,
      free_meets_remaining: 2
    };
  }
  
  // Check local premium status first (instant check for better UX)
  const localPurchaseData = localStorage.getItem('foresight_purchase_data');
  if (localPurchaseData) {
    const parsed = JSON.parse(localPurchaseData);
    if (parsed.isPremium === true) {
      // User is premium locally, trust this
      return {
        device_id: deviceId,
        is_premium: true,
        meets_created: parsed.meetsCreated || 0,
        free_meets_remaining: 0,
        purchase_date: parsed.premiumPurchaseDate
      };
    }
  }
  
  try {
    const response = await fetch(
      `${API_URL}/api/purchase/status?device_id=${encodeURIComponent(deviceId)}`,
      authFetchOptions()
    );
    
    if (!response.ok) {
      throw new Error('Failed to fetch purchase status');
    }
    
    const data = await response.json();
    
    // Cache locally
    localStorage.setItem('foresight_purchase_status', JSON.stringify(data));
    
    return data;
  } catch (error) {
    console.error('Get purchase status error:', error);
    // Fallback to localStorage
    const cached = localStorage.getItem('foresight_purchase_status');
    if (cached) {
      return JSON.parse(cached);
    }
    return {
      is_premium: false,
      meets_created: 0,
      free_meets_remaining: 2
    };
  }
};

/**
 * Record a purchase (called after IAP validation)
 */
export const recordPurchase = async (productId) => {
  const deviceId = getDeviceId();
  if (!deviceId) {
    throw new Error('Device not registered');
  }
  
  try {
    const response = await fetch(
      `${API_URL}/api/purchase/record?device_id=${encodeURIComponent(deviceId)}&product_id=${encodeURIComponent(productId)}`,
      authFetchOptions({ method: 'POST' })
    );
    
    if (!response.ok) {
      throw new Error('Failed to record purchase');
    }
    
    const data = await response.json();
    
    // Update local cache with full premium status
    const cached = JSON.parse(localStorage.getItem('foresight_purchase_status') || '{}');
    cached.device_id = deviceId;
    cached.is_premium = true;
    cached.purchase_date = new Date().toISOString();
    cached.free_meets_remaining = 0; // Premium users don't need free meets
    localStorage.setItem('foresight_purchase_status', JSON.stringify(cached));
    
    // Also update the old localStorage purchase data for backwards compatibility
    const oldPurchaseData = JSON.parse(localStorage.getItem('foresight_purchase_data') || '{}');
    oldPurchaseData.isPremium = true;
    oldPurchaseData.premiumPurchaseDate = new Date().toISOString();
    localStorage.setItem('foresight_purchase_data', JSON.stringify(oldPurchaseData));
    
    return data;
  } catch (error) {
    console.error('Record purchase error:', error);
    throw error;
  }
};


// ============== SYNC UTILITIES ==============

/**
 * Is the browser currently online?
 */
export const isOnline = () => {
  if (typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean') {
    return navigator.onLine;
  }
  return true;
};

const SYNC_STATE_KEY = 'foresight_sync_state';

const readLocalMeets = () => JSON.parse(localStorage.getItem('foresight_meets') || '[]');
const writeLocalMeets = (list) => localStorage.setItem('foresight_meets', JSON.stringify(list));

/**
 * Replace a meet in localStorage by id.
 *  - If replacement.id differs from oldId, the old entry is removed and the replacement inserted.
 *  - Strips _offline / _offline_origin flags on the replacement.
 */
const replaceLocalMeet = (oldId, replacement) => {
  const meets = readLocalMeets();
  const filtered = meets.filter(m => String(m.id) !== String(oldId) && String(m.id) !== String(replacement.id));
  // eslint-disable-next-line no-unused-vars
  const { _offline, _offline_origin, ...clean } = replacement;
  filtered.push(clean);
  writeLocalMeets(filtered);
};

/**
 * Sync a single offline-tagged meet to the backend.
 * Returns { synced: boolean, reason?: string, newId?: string }.
 */
const syncOneMeet = async (meet) => {
  const deviceId = getDeviceId();
  // eslint-disable-next-line no-unused-vars
  const { _offline, _offline_origin, id, device_id, created, updated, ...payload } = meet;
  const isCreate = _offline_origin === 'create' || String(id).length < 13; // heuristic: local timestamp ids are 13-digit strings

  // Prefer the explicit origin tag, fall back to "try update first" if origin is missing.
  if (isCreate) {
    try {
      const response = await fetch(`${API_URL}/api/meets`, authFetchOptions({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_id: deviceId, ...payload })
      }));
      if (!response.ok) {
        if (response.status === 402) {
          return { synced: false, reason: 'paywall' };
        }
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${response.status}`);
      }
      const serverMeet = await response.json();
      replaceLocalMeet(id, serverMeet);
      return { synced: true, newId: serverMeet.id };
    } catch (error) {
      return { synced: false, reason: error.message };
    }
  }

  // Update path — meet already exists in backend, push latest payload
  try {
    const response = await fetch(
      `${API_URL}/api/meets/${encodeURIComponent(id)}?device_id=${encodeURIComponent(deviceId)}`,
      authFetchOptions({
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
    );
    if (response.status === 404) {
      // Server doesn't have this meet (e.g. device re-registered) — fall back to POST.
      return syncOneMeet({ ...meet, _offline_origin: 'create' });
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || `HTTP ${response.status}`);
    }
    const serverMeet = await response.json();
    replaceLocalMeet(id, serverMeet);
    return { synced: true, newId: serverMeet.id };
  } catch (error) {
    return { synced: false, reason: error.message };
  }
};

/**
 * Push all offline-tagged meets to the backend.
 * Returns { attempted, synced, failed, paywalled }.
 * Safe to call anytime; concurrent calls are de-duped via an in-flight lock.
 */
let syncInFlight = null;
export const syncOfflineMeets = async () => {
  if (!getDeviceId()) return { attempted: 0, synced: 0, failed: 0, paywalled: 0 };
  if (!isOnline()) return { attempted: 0, synced: 0, failed: 0, paywalled: 0 };
  if (syncInFlight) return syncInFlight;

  syncInFlight = (async () => {
    const offlineMeets = readLocalMeets().filter(m => m._offline);
    let synced = 0, failed = 0, paywalled = 0;

    for (const meet of offlineMeets) {
      const result = await syncOneMeet(meet);
      if (result.synced) {
        synced += 1;
      } else if (result.reason === 'paywall') {
        paywalled += 1;
      } else {
        failed += 1;
      }
    }

    const summary = {
      attempted: offlineMeets.length,
      synced,
      failed,
      paywalled,
      at: new Date().toISOString()
    };
    try {
      localStorage.setItem(SYNC_STATE_KEY, JSON.stringify(summary));
    } catch (e) { /* ignore quota */ }
    return summary;
  })();

  try {
    return await syncInFlight;
  } finally {
    syncInFlight = null;
  }
};

/**
 * Read the outcome of the most recent sync (for surfacing UI toasts).
 */
export const getLastSyncSummary = () => {
  try {
    return JSON.parse(localStorage.getItem(SYNC_STATE_KEY) || 'null');
  } catch {
    return null;
  }
};

/**
 * How many local meets still need to be pushed to the backend?
 */
export const getPendingOfflineCount = () => {
  return readLocalMeets().filter(m => m._offline).length;
};

/**
 * Flush any meet-end POSTs that autoEndMeet couldn't deliver in real time
 * (because the device was offline, or the app was force-quit before the
 * fetch completed). Called at app start and on every `online` event so the
 * backend's `is_active` flag eventually catches up to the user's reality —
 * otherwise reopening the app would falsely report "another meet is live"
 * the next time the user tries to start a different meet.
 */
export const flushPendingMeetEnds = async () => {
  if (!isOnline()) return { attempted: 0, ended: 0 };
  let queue;
  try {
    queue = JSON.parse(localStorage.getItem('foresight_pending_end') || '[]');
  } catch {
    queue = [];
  }
  if (!Array.isArray(queue) || queue.length === 0) return { attempted: 0, ended: 0 };

  const remaining = [];
  let ended = 0;
  for (const entry of queue) {
    if (!entry || !entry.meet_id) continue;
    try {
      const res = await fetch(`${API_URL}/api/live-meet/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ meet_id: entry.meet_id, user_id: entry.user_id || '' })
      });
      if (res.ok || res.status === 404) {
        // 404 = backend already considers it inactive, treat as success.
        ended += 1;
        // Now that the backend has confirmed end, it is safe to drop the
        // live-meet pointer IF the pointer is still pointing at this meet
        // (autoEndMeet defers pointer removal until backend confirms — see
        // PRD invariant on offline-end pointer survival).
        try {
          const liveMeet = JSON.parse(localStorage.getItem('foresight_live_meet') || '{}');
          if (liveMeet && (liveMeet.meetId === entry.meet_id || String(liveMeet.meetId) === String(entry.meet_id))) {
            localStorage.removeItem('foresight_live_meet');
          }
        } catch (_) { /* ignore */ }
      } else {
        remaining.push(entry);
      }
    } catch (_) {
      // Network failure — keep in queue.
      remaining.push(entry);
    }
  }
  if (remaining.length) {
    localStorage.setItem('foresight_pending_end', JSON.stringify(remaining));
  } else {
    localStorage.removeItem('foresight_pending_end');
  }
  return { attempted: queue.length, ended };
};

export default {
  registerDevice,
  getDeviceId,
  getMeets,
  getMeet,
  verifyMeetExists,
  createMeet,
  updateMeet,
  deleteMeet,
  getSettings,
  saveSettings,
  getPurchaseStatus,
  recordPurchase,
  syncOfflineMeets,
  getPendingOfflineCount,
  getLastSyncSummary,
  isOnline,
  flushPendingMeetEnds
};
